/**
 * Front-end observability bootstrap: Sentry (errors) + PostHog (product events).
 *
 * Call `initObservability()` once, as early as possible in the app entry. Both
 * backends are INERT until their keys are present (VITE_SENTRY_DSN /
 * VITE_POSTHOG_KEY), so this is safe to always call. Reads config from Vite env
 * (cast, since these keys aren't in the generated ImportMetaEnv types).
 */
import {
  captureException,
  captureTransaction,
  initSentry,
  sentryEnabled,
  setSentryUser,
} from "./sentry";
import { capture, identify, initPostHog, pageview, posthogEnabled, resetIdentity } from "./posthog";
import { resolveTelemetryEnvironment } from "./environment";
import { isUnsupportedMediaSourceError } from "./mediaErrors";
import { isResizeObserverLoopNotice } from "./resizeObserverNoise";

export { captureException } from "./sentry";
export { capture as trackEvent } from "./posthog";

const env = import.meta.env as unknown as Record<string, string | undefined>;

// Shared-project public client values (Sentry DSN + PostHog write-only key). These are
// PUBLIC by design — the DSN is embedded in every browser bundle, and the PostHog key is
// a write-only client key ("safe to use in public apps"), so committing them is fine and
// carries no secret. Used as the DEFAULT so the build always has working telemetry;
// Cloudflare Pages env vars (VITE_SENTRY_DSN / VITE_POSTHOG_KEY) still OVERRIDE when set.
const DEFAULT_SENTRY_DSN =
  "https://76f3a24b19c39bdd5b44a32e2ccc0910@o4512169450471424.ingest.us.sentry.io/4512169452699648";
const DEFAULT_POSTHOG_KEY = "phc_tCCbCS8fc5vuR2Jqm2VqsX8uvefz4bKpfNLCUEH9KC8o";

let installed = false;

export function initObservability(): void {
  if (installed) return;
  installed = true;

  const rateRaw = env.VITE_SENTRY_TRACES_SAMPLE_RATE;
  initSentry({
    dsn: env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN,
    // NOT `import.meta.env.MODE`: that is "production" for the staging Pages
    // project too, which merged staging noise and real production incidents into
    // one Sentry bucket. See observability/environment.ts for the full reasoning.
    environment: resolveTelemetryEnvironment({
      explicit: env.VITE_SENTRY_ENVIRONMENT,
      backendUrl: env.VITE_HTTP_URL,
      ...(typeof location !== "undefined" ? { hostname: location.hostname } : {}),
      mode: import.meta.env.MODE,
    }),
    release: env.VITE_APP_RELEASE,
    tracesSampleRate: rateRaw != null && rateRaw !== "" ? Number(rateRaw) : undefined,
  });
  initPostHog({ key: env.VITE_POSTHOG_KEY || DEFAULT_POSTHOG_KEY, host: env.VITE_POSTHOG_HOST });

  if (typeof window === "undefined") return;

  // Page-load timing → a Sentry transaction (once the load event has fired so the
  // navigation entry is complete). Sampled + inert without a DSN.
  if (document.readyState === "complete") capturePageLoad();
  else window.addEventListener("load", () => capturePageLoad(), { once: true });

  // Global safety nets: nothing uncaught should vanish into the console only —
  // except a media element reporting it has no playable source, which is a
  // handled playback failure the player surfaces to the user itself. See
  // observability/mediaErrors.ts for why that one is dropped here.
  window.addEventListener("error", (e) => {
    if (isUnsupportedMediaSourceError(e.error)) return;
    // The ResizeObserver loop notice arrives with NO error object — only a message — so both
    // are checked. See observability/resizeObserverNoise.ts for why it is not an error at all.
    if (isResizeObserverLoopNotice(e.error) || isResizeObserverLoopNotice(e.message)) return;
    captureException(e.error ?? e.message ?? "window.onerror");
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isUnsupportedMediaSourceError(e.reason)) return;
    if (isResizeObserverLoopNotice(e.reason)) return;
    captureException(e.reason ?? "unhandledrejection");
  });

  if (posthogEnabled()) pageview();

  // Identify the signed-in user once Clerk resolves. Clerk loads async, so watch
  // briefly for the user id and bind it to both backends, then stop.
  if (sentryEnabled() || posthogEnabled()) watchClerkUser();
}

/** Poll briefly for the Clerk user id, identify once, then clear the watcher. */
function watchClerkUser(): void {
  let ticks = 0;
  let lastId: string | null = null;
  const timer = window.setInterval(() => {
    ticks += 1;
    const clerk = (window as { Clerk?: { loaded?: boolean; user?: { id?: string } } }).Clerk;
    const id = clerk?.user?.id ?? null;
    if (id && id !== lastId) {
      lastId = id;
      setSentryUser({ id });
      identify(id);
    } else if (!id && clerk?.loaded === true && lastId) {
      // Signed out after having been identified.
      lastId = null;
      setSentryUser(null);
      resetIdentity();
    }
    // Stop after Clerk has settled (identified or confirmed signed-out), or ~30s.
    if ((clerk?.loaded === true && (id || ticks > 4)) || ticks > 60) {
      window.clearInterval(timer);
    }
  }, 500);
}

/** Send the browser's navigation-timing breakdown as a Sentry "pageload" transaction. */
function capturePageLoad(): void {
  if (typeof performance === "undefined" || typeof performance.getEntriesByType !== "function") {
    return;
  }
  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!nav) return;
  const origin = performance.timeOrigin;
  const startMs = origin + nav.startTime;
  const endMs = origin + (nav.loadEventEnd || nav.responseEnd);
  if (!(endMs > startMs)) return;
  const span = (op: string, s: number, e: number) => ({
    op,
    startMs: origin + s,
    endMs: origin + e,
  });
  const spans = [
    span("browser.dns", nav.domainLookupStart, nav.domainLookupEnd),
    span("browser.connect", nav.connectStart, nav.connectEnd),
    span("browser.request", nav.requestStart, nav.responseStart),
    span("browser.response", nav.responseStart, nav.responseEnd),
    span("browser.dom", nav.responseEnd, nav.domContentLoadedEventEnd),
    span("browser.load", nav.domContentLoadedEventEnd, nav.loadEventEnd),
  ].filter((s) => s.endMs > s.startMs);
  captureTransaction({
    name: location.pathname,
    op: "pageload",
    startMs,
    endMs,
    spans,
    tags: { route: location.pathname },
  });
}

/**
 * Time a front-end action → a Sentry transaction. Call at the start; invoke the returned
 * function when it completes. Near-zero cost when tracing is off (just two timestamps +
 * a sampled fire-and-forget POST). e.g.
 *   const done = startTiming("send-message", "ui.action"); … ; done();
 */
export function startTiming(name: string, op = "ui.action"): () => void {
  const now = () =>
    typeof performance !== "undefined" ? performance.timeOrigin + performance.now() : Date.now();
  const startMs = now();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    captureTransaction({ name, op, startMs, endMs: now() });
  };
}
